import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileBarChart2, Network, Tag, Building2, Landmark, Warehouse, Boxes, Layers, Gauge } from 'lucide-react';
import MappingModal from './Imports/MappingModal';
import ImportModal from './Imports/ImportModal';
import { IMPORT_CONFIGS } from './Imports/ImportConfig';
import { ListSection, ListRow, PrimaryButton, SecondaryButton } from '../components/RowList';

const Imports = () => {
  const navigate = useNavigate();
  const [mappingConfig, setMappingConfig] = useState(null);
  const [importConfig, setImportConfig] = useState(null);

  // One entry per md_* table, driving BOTH sections below: `masterType` keys
  // into the backend MASTER_SPECS for the add/edit page, `config` keys into
  // IMPORT_CONFIGS for the file import. Same table either way.
  const masterCards = [
    {
      config: IMPORT_CONFIGS.gl_accounts,
      masterType: 'gl_accounts',
      icon: FileBarChart2,
      title: 'GL Codes',
      editDescription: 'Add, edit and retire accounts in the chart of accounts — category, normal balance and hierarchy.',
      description: 'Import Chart of Accounts updates and account hierarchy structures.',
    },
    {
      config: IMPORT_CONFIGS.cost_centers,
      masterType: 'cost_centers',
      icon: Network,
      title: 'Cost Centres',
      editDescription: 'Add, edit and retire cost centres and their parent hierarchy.',
      description: 'Manage and import cost centre dimensions, department allocations, and organizational hierarchies.',
    },
    {
      config: IMPORT_CONFIGS.stock_codes,
      masterType: 'stock_codes',
      icon: Tag,
      title: 'Stock Codes',
      editDescription: 'Add, edit and retire items — unit of measure, alternate units (pallet, tonne), item type, product class and valuation.',
      description: 'Update inventory master files, SKUs, product classes, unit conversions, and base pricing details.',
    },
    {
      config: IMPORT_CONFIGS.sites,
      masterType: 'sites',
      icon: Building2,
      title: 'Sites',
      editDescription: 'Add, edit and retire plants, depots and branches.',
      description: 'Import site/location master records, regions, and operational facilities.',
    },
    {
      config: IMPORT_CONFIGS.warehouses,
      masterType: 'warehouses',
      icon: Warehouse,
      title: 'Warehouses',
      editDescription: 'Add, edit and retire warehouses, the site each belongs to, and which is primary for costing.',
      description: 'Import warehouse master records and the site each one belongs to.',
    },
    {
      config: IMPORT_CONFIGS.cost_drivers,
      masterType: 'cost_drivers',
      icon: Gauge,
      title: 'Cost Drivers',
      editDescription: 'Add, edit and retire the operational quantities variable costs scale with — throughput, floor area, energy, hours.',
      description: 'Import the cost-driver catalogue used by variable running costs across Distribution and Manufacturing.',
    },
  ];

  return (
    // SCROLL WRAPPER APPLIED HERE
    <div className="h-full overflow-y-auto font-sans bg-slate-100 dark:bg-slate-900">
      <div className="max-w-6xl mx-auto px-6 py-10">

        <div className="mb-10">
          <h1 className="m-0 text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Master Data</h1>
          <p className="mt-2 text-base text-slate-500 dark:text-slate-400">
            Maintain the codes every budget is built on — by hand for a change or two, by file for a load.
          </p>
        </div>

        {/* SECTION 1: Maintain by hand.
            First, because a single correction is the common case and reaching
            for a spreadsheet to fix one description is the wrong shape of work.
            Same records as the imports below, same tables. */}
        <ListSection title="Master Data">
          {masterCards.map((card) => (
            <ListRow
              key={`edit-${card.config.key}`}
              icon={card.icon}
              title={card.title}
              description={card.editDescription}
              actions={<PrimaryButton onClick={() => navigate(`/master-data/${card.masterType}`)}>Add / Edit</PrimaryButton>}
            />
          ))}
        </ListSection>

        {/* SECTION 2: Bulk load the same tables from a file. */}
        <ListSection title="Master Data Imports">
          {masterCards.map((card) => (
            <ListRow
              key={card.config.key}
              icon={card.icon}
              title={card.title}
              description={card.description}
              actions={
                <>
                  <SecondaryButton onClick={() => setMappingConfig(card.config)}>Map Mappings</SecondaryButton>
                  <PrimaryButton onClick={() => setImportConfig(card.config)}>Upload File</PrimaryButton>
                </>
              }
            />
          ))}
        </ListSection>

        {/* SECTION 3: Actuals */}
        <ListSection title="Actuals Imports">
          <ListRow
            icon={Landmark}
            title="General Ledger Actuals"
            description="Import monthly trial balances, journal entries, and finalized financial periods."
            actions={
              <>
                <SecondaryButton onClick={() => alert('Actuals mapping coming soon!')}>Map Mappings</SecondaryButton>
                <PrimaryButton onClick={() => alert('Actuals import coming soon!')}>Upload File</PrimaryButton>
              </>
            }
          />
          <ListRow
            icon={Boxes}
            title="Stock Actuals"
            description="Import actual stock cost per warehouse, stock code and financial period."
            actions={
              <>
                <SecondaryButton onClick={() => setMappingConfig(IMPORT_CONFIGS.stock_actuals)}>Map Mappings</SecondaryButton>
                <PrimaryButton onClick={() => setImportConfig(IMPORT_CONFIGS.stock_actuals)}>Upload File</PrimaryButton>
              </>
            }
          />
          <ListRow
            icon={Layers}
            title="BOM Actuals"
            description="Import the actual as-built bill of materials per site and year — parent item, component, quantity and losses — into act_bom for budget-vs-actual comparison."
            actions={
              <>
                <SecondaryButton onClick={() => setMappingConfig(IMPORT_CONFIGS.bom_actuals)}>Map Mappings</SecondaryButton>
                <PrimaryButton onClick={() => setImportConfig(IMPORT_CONFIGS.bom_actuals)}>Upload File</PrimaryButton>
              </>
            }
          />
        </ListSection>

        {/* Shared Mapping Modal */}
        <MappingModal
          isOpen={!!mappingConfig}
          config={mappingConfig}
          onClose={() => setMappingConfig(null)}
        />

        {/* Shared Import Modal */}
        <ImportModal
          isOpen={!!importConfig}
          config={importConfig}
          onClose={() => setImportConfig(null)}
        />

      </div>
    </div>
  );
};

export default Imports;
